import { GradientText, RightIcon } from "lola-framework-ui-test";
import { type JSX, useEffect, useMemo, useRef, useState } from "react";
import { getEvents, safeCapture } from "../analytics";
import { useStore } from "../store/globalState";
import { formatMoney } from "../utils";

export interface QuoteInfoProps {
  label: string;
  amount?: string;
  icon?: JSX.Element;
  currency: string;
  textColor: string;
  isActive?: boolean;
  mode?: "info" | "input";
  /** Render a downward chevron after the currency to read as a selector. */
  showSelectorChevron?: boolean;
  /** Optional content rendered inside the box, below the amount row. */
  bottomSlot?: JSX.Element;
  /** Hide the leading "$" currency symbol on the amount. */
  hideCurrencySymbol?: boolean;
  /** Fired when the currency chip is tapped (used to open a picker). */
  onSelect?: () => void;
}

const MAX_FONT = 2.5;
const MIN_FONT = 1.2;

/** Strip grouping / currency noise into a raw editable amount string. */
export function rawAmountFromDisplay(value: string | undefined): string {
  if (!value) return "";
  const cleaned = value.replace(/[^\d.]/g, "");
  if (!cleaned) return "";
  const dotIndex = cleaned.indexOf(".");
  const intPart =
    dotIndex === -1 ? cleaned : cleaned.substring(0, dotIndex);
  const decPart =
    dotIndex === -1
      ? ""
      : cleaned.substring(dotIndex + 1).replace(/\./g, "").slice(0, 2);
  if (!intPart && !decPart && dotIndex === -1) return "";
  if (dotIndex === -1) return intPart;
  return `${intPart || "0"}.${decPart}`;
}

/**
 * Prefer a typing-friendly raw string: drop trailing padded `.00` so focusing
 * a stored "5.00" does not immediately show decimals (caret/face mismatch).
 */
function rawForEditing(value: string | undefined): string {
  const raw = rawAmountFromDisplay(value);
  if (!raw) return "";
  if (/^\d+\.00$/.test(raw)) return raw.slice(0, -3);
  return raw;
}

/** Integer digits only; decimals stay capped at 2. Max display: 9,999,999,999.99 */
const MAX_INTEGER_DIGITS = 10;

export const QuoteInfo: React.FC<QuoteInfoProps> = ({
  label,
  amount,
  icon,
  currency,
  isActive = false,
  textColor,
  mode = "info",
  showSelectorChevron = false,
  bottomSlot,
  hideCurrencySymbol = false,
  onSelect,
}) => {
  const { amountState, setAmountState, theme } = useStore();
  const [amountInput, setAmountInput] = useState<string>(() =>
    mode === "input" ? rawForEditing(amountState.amount) : ""
  );
  const [focused, setFocused] = useState(false);
  const focusedRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [fontSize, setFontSize] = useState(MAX_FONT);
  const colorConfig = theme.whiteView;
  const { bodyCopy } = colorConfig;
  const inputId = "amount-input";

  // Sync store → local only when the user is NOT typing (keeps blurred face
  // as N.NN with small decimals) — send / deferred parity.
  useEffect(() => {
    if (mode !== "input" || focusedRef.current) return;
    setAmountInput(rawAmountFromDisplay(amountState.amount));
  }, [mode, amountState.amount]);

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const probe =
      inputRef.current ??
      (container.querySelector(".label-amount") as HTMLElement | null);
    const styles = probe
      ? getComputedStyle(probe)
      : getComputedStyle(container);
    const fontFamily = styles.fontFamily;
    const fontWeight = styles.fontWeight;

    const measureAtMax = (text: string) => {
      const span = document.createElement("span");
      span.style.fontSize = `${MAX_FONT}rem`;
      span.style.fontFamily = fontFamily;
      span.style.fontWeight = fontWeight;
      span.style.whiteSpace = "nowrap";
      span.innerText = text;
      document.body.appendChild(span);
      const width = span.offsetWidth;
      document.body.removeChild(span);
      return width;
    };

    const CHIP_RESERVE_PX = 150;
    const SYMBOL_MARGIN_PX = 4;
    const prefix = hideCurrencySymbol ? "" : "$";
    const visibleAmount =
      mode === "input"
        ? inputRef.current?.value || amountInput || "0"
        : amount?.trim() || "0";
    const symbolWidth = prefix
      ? measureAtMax(prefix) + SYMBOL_MARGIN_PX
      : 0;
    const textWidth = measureAtMax(visibleAmount || "0");
    const available = Math.max(
      container.offsetWidth - CHIP_RESERVE_PX - symbolWidth,
      1
    );
    const scale = available / textWidth;
    let newFont = MAX_FONT * scale;
    newFont = Math.min(MAX_FONT, Math.max(MIN_FONT, newFont));
    setFontSize(newFont);
  }, [mode, amountInput, amount, hideCurrencySymbol]);

  const normalizeRawAmount = (value: string) => {
    const cleaned = value.replace(/[^\d.]/g, "");
    const dotIndex = cleaned.indexOf(".");
    const intPart =
      dotIndex === -1 ? cleaned : cleaned.substring(0, dotIndex);
    const decPart =
      dotIndex === -1
        ? ""
        : cleaned.substring(dotIndex + 1).replace(/\./g, "").slice(0, 2);
    const hasDot = dotIndex !== -1;
    return { intPart, decPart, hasDot };
  };

  const toRawString = ({
    intPart,
    decPart,
    hasDot,
  }: ReturnType<typeof normalizeRawAmount>) => {
    if (!intPart && !decPart && !hasDot) return "";
    const safeInt = intPart || (hasDot ? "0" : "");
    return hasDot ? `${safeInt}.${decPart}` : safeInt;
  };

  const formatWithGrouping = (raw: string) => {
    if (!raw) return "";
    const [intPart = "", decPart = ""] = raw.split(".");
    const groupedInt =
      intPart.length > 0
        ? intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
        : "0";
    return raw.includes(".") ? `${groupedInt}.${decPart}` : groupedInt;
  };

  const getNormalizedFromDisplay = (value: string, caretPos: number) => {
    let raw = "";
    let rawCaret = 0;
    let hasDot = false;

    for (let i = 0; i < value.length; i += 1) {
      const ch = value[i];
      if (/\d/.test(ch)) {
        raw += ch;
        if (i < caretPos) rawCaret += 1;
      } else if (ch === "." && !hasDot) {
        hasDot = true;
        raw += ".";
        if (i < caretPos) rawCaret += 1;
      }
    }

    const normalized = normalizeRawAmount(raw);
    const normalizedRaw = toRawString(normalized);
    const clampedCaret = Math.min(rawCaret, normalizedRaw.length);
    return { normalizedRaw, clampedCaret };
  };

  const getDisplayCaretFromRaw = (displayValue: string, rawCaret: number) => {
    if (rawCaret <= 0) return 0;
    let count = 0;
    for (let i = 0; i < displayValue.length; i += 1) {
      const ch = displayValue[i];
      if (/\d/.test(ch) || ch === ".") {
        count += 1;
        if (count === rawCaret) return i + 1;
      }
    }
    return displayValue.length;
  };

  const onChangeAmount = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const { value } = event.currentTarget;
    const caretPos = input.selectionStart ?? 0;
    const { normalizedRaw, clampedCaret } = getNormalizedFromDisplay(
      value,
      caretPos
    );
    const normalizedParts = normalizeRawAmount(normalizedRaw);
    const limitedIntPart =
      normalizedParts.intPart.length > MAX_INTEGER_DIGITS
        ? normalizedParts.intPart.slice(0, MAX_INTEGER_DIGITS)
        : normalizedParts.intPart;
    const nextRaw = toRawString({
      ...normalizedParts,
      intPart: limitedIntPart,
    });
    const nextCaret = Math.min(clampedCaret, nextRaw.length);
    const formattedDisplay = formatWithGrouping(nextRaw);

    // Keep the typed raw in the store (no ".00" padding) so pricing can parse
    // it and the sync effect cannot fight the caret mid-keystroke.
    setAmountInput(nextRaw);
    setAmountState({ amount: nextRaw });

    requestAnimationFrame(() => {
      if (!inputRef.current) return;
      const newCaret = getDisplayCaretFromRaw(formattedDisplay, nextCaret);
      inputRef.current.setSelectionRange(newCaret, newCaret);
    });
  };

  const inputWidth = useMemo(() => {
    const displayValue = formatWithGrouping(amountInput);
    if (!displayValue) return "7.5rem";
    // `ch` tracks this wrap's font-size so glyphs are not clipped.
    return `${Math.max(displayValue.length, 1) + 0.75}ch`;
  }, [amountInput, fontSize]);

  const handleFocus = () => {
    focusedRef.current = true;
    setFocused(true);
    if (mode !== "input") return;
    // Strip trailing ".00" so typing does not start mid-decimals.
    setAmountInput(rawForEditing(amountInput));
  };

  const handleBlur = () => {
    focusedRef.current = false;
    setFocused(false);
    if (mode !== "input") return;

    // Normalize to N.NN for the blurred face (small decimals) + store.
    const padded = formatMoney(amountInput) || "";
    setAmountInput(padded);
    setAmountState({ amount: padded });

    const numeric = parseFloat(padded || amountInput);
    if (!padded || Number.isNaN(numeric) || numeric <= 0) return;
    safeCapture(getEvents().AMOUNT_SUBMITTED, {
      amount: numeric,
      anchor: "receive",
      destination_currency: currency,
    });
  };

  const focusInput = () => {
    inputRef.current?.focus();
  };

  const inputValue = formatWithGrouping(amountInput);

  const splitAmount = amount?.trim() ? amount.split(".") : ["0", "00"];
  const mutedColor = `${textColor}80`;
  const hasInputAmount = amountInput.length > 0;
  const hasInfoAmount = Boolean(amount?.trim());
  const symbolColor = focused || hasInputAmount ? textColor : mutedColor;
  const infoAmountColor = hasInfoAmount ? textColor : mutedColor;
  const [inputInt = "", inputDec = ""] = inputValue.split(".");
  const inputHasDot = inputValue.includes(".");
  const chipBg = "transparent";
  const chipColor = bodyCopy;

  const mainRow = (
    <>
      <div className="quote-send-info" onClick={focusInput}>
        <GradientText textColor={textColor} className="qoute-label">
          {label}
        </GradientText>
        {mode === "info" && (
          <div
            className="amount-input-row"
            style={{ fontSize: `${fontSize}rem` }}
          >
            <GradientText
              textColor={infoAmountColor}
              className="highlight label-amount"
            >
              {hideCurrencySymbol ? "" : "$"}
              {splitAmount[0]}.<label>{splitAmount[1]}</label>
            </GradientText>
          </div>
        )}
        {mode === "input" && (
          <div className="amount-input-row">
            {!hideCurrencySymbol && (
              <label
                className="amount-label"
                htmlFor={inputId}
                style={{
                  fontSize: `${fontSize}rem`,
                  color: symbolColor,
                  marginRight: 4,
                }}
              >
                $
              </label>
            )}
            <div
              className="amount-input-wrap"
              style={{
                width: inputWidth,
                maxWidth: "100%",
                fontSize: `${fontSize}rem`,
                color: hasInputAmount || focused ? textColor : mutedColor,
              }}
            >
              {/*
                Face always paints the glyphs (same bold/typeface focused or not).
                Focused → decimals full size. Blurred → smaller decimals.
                Transparent input only owns the caret.
              */}
              <div
                className="amount-input-face label-amount"
                style={{ color: hasInputAmount ? textColor : mutedColor }}
                aria-hidden
              >
                {hasInputAmount ? (
                  <>
                    {inputInt || "0"}
                    {inputHasDot ? (
                      focused ? (
                        <>.{inputDec}</>
                      ) : (
                        <>
                          .<label>{inputDec}</label>
                        </>
                      )
                    ) : null}
                  </>
                ) : (
                  <>
                    0.<label>00</label>
                  </>
                )}
              </div>
              {/*
                Stays type="text" (not "number") because the display carries
                grouping commas and this input owns caret placement;
                inputMode is what asks mobile for the decimal keypad.
              */}
              <input
                ref={inputRef}
                type="text"
                inputMode="decimal"
                enterKeyHint="done"
                autoComplete="off"
                style={
                  {
                    width: "100%",
                    fontSize: `${fontSize}rem`,
                    caretColor: textColor,
                    ["--amount-placeholder" as string]: mutedColor,
                  } as React.CSSProperties
                }
                id={inputId}
                placeholder="0.00"
                className="amount-input"
                value={inputValue === "NaN" ? amountInput : inputValue}
                onChange={onChangeAmount}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            </div>
          </div>
        )}
      </div>
      <section
        className={`chip border-special ${
          showSelectorChevron ? "chip-selector" : ""
        } ${onSelect ? "chip-clickable" : ""}`.trim()}
        style={
          {
            background: chipBg,
            "--bgr": textColor,
          } as React.CSSProperties
        }
        onClick={(event) => {
          event.stopPropagation();
          onSelect?.();
        }}
        role={onSelect ? "button" : undefined}
      >
        {icon && icon}

        <GradientText textColor={chipColor}>{currency}</GradientText>

        {showSelectorChevron && (
          <span className="chip-chevron" aria-hidden="true">
            <RightIcon size={16} colors={[chipColor, chipColor]} />
          </span>
        )}
      </section>
    </>
  );

  return (
    <section
      className={`quote-send ${isActive ? "active" : ""} ${mode} ${
        bottomSlot ? "has-bottom-slot" : ""
      }`.trim()}
      ref={containerRef}
    >
      <div className="quote-send-main">{mainRow}</div>
      {bottomSlot ? (
        <div className="quote-send-bottom">{bottomSlot}</div>
      ) : null}
    </section>
  );
};
